/**
 * Fishing Spot Rendering System
 * 
 * Handles rendering of fishing spots as animated water tiles.
 * Fishing spots are single tiles in the ocean that players can interact with.
 */

import Phaser from 'phaser';
import { TILE_SIZE, FishingSpotData, FishingSpotType, FISHING_SPOT_COLORS } from '../types';

export interface FishingSpotInfo {
  id: string;
  tileX: number;
  tileY: number;
  type: FishingSpotType;
  worldX: number;
  worldY: number;
}

export interface FishingSpotRendererCallbacks {
  onFishingSpotClick: (spot: FishingSpotInfo) => void;
  onFishingSpotRightClick?: (spot: FishingSpotInfo, screenX: number, screenY: number) => void;
}

interface FishingSpotSprite {
  graphics: Phaser.GameObjects.Graphics;
  hitArea: Phaser.GameObjects.Rectangle;
  label?: Phaser.GameObjects.Text;
  chunkId: string;
  spotData: FishingSpotInfo;
}

export class FishingSpotRenderer {
  private scene: Phaser.Scene;
  private spots: Map<string, FishingSpotSprite> = new Map();
  private callbacks: FishingSpotRendererCallbacks;
  private animationTimer: Phaser.Time.TimerEvent | null = null;
  private animationPhase: number = 0;

  constructor(scene: Phaser.Scene, callbacks: FishingSpotRendererCallbacks) {
    this.scene = scene;
    this.callbacks = callbacks;
    this.startAnimation();
  }

  /**
   * Start the ripple animation for fishing spots
   */
  private startAnimation(): void {
    this.animationTimer = this.scene.time.addEvent({
      delay: 500,  // Update every 500ms
      callback: () => {
        this.animationPhase = (this.animationPhase + 1) % 4;
        this.updateAnimations();
      },
      loop: true,
    });
  }

  /**
   * Update all fishing spot animations
   */
  private updateAnimations(): void {
    this.spots.forEach((sprite) => {
      this.drawFishingSpot(sprite.graphics, sprite.spotData, this.animationPhase);
    });
  }

  /**
   * Get all fishing spots
   */
  getSpots(): Map<string, FishingSpotSprite> {
    return this.spots;
  }

  /**
   * Clear all fishing spots (used on world reset)
   */
  clearSpots(): void {
    this.spots.forEach((sprite) => {
      sprite.graphics.destroy();
      sprite.hitArea?.destroy();
    });
    this.spots.clear();
    console.log('🗑️ Cleared all fishing spots from renderer');
  }

  /**
   * Render fishing spots from chunk data
   */
  renderFishingSpots(fishingSpots: FishingSpotData[], chunkId: string): void {
    for (const spot of fishingSpots) {
      this.createFishingSpotSprite(spot, chunkId);
    }
  }

  /**
   * Create a fishing spot sprite
   */
  private createFishingSpotSprite(spot: FishingSpotData, chunkId: string): void {
    if (this.spots.has(spot.id)) return;

    const worldX = spot.tileX * TILE_SIZE + TILE_SIZE / 2;
    const worldY = spot.tileY * TILE_SIZE + TILE_SIZE / 2;

    const spotInfo: FishingSpotInfo = {
      id: spot.id,
      tileX: spot.tileX,
      tileY: spot.tileY,
      type: spot.type,
      worldX,
      worldY,
    };

    // Create graphics for the fishing spot
    const graphics = this.scene.add.graphics();
    graphics.setDepth(5);  // Above water, below ships
    this.drawFishingSpot(graphics, spotInfo, this.animationPhase);

    // Create invisible hitbox for interaction
    const hitArea = this.scene.add.rectangle(worldX, worldY, TILE_SIZE, TILE_SIZE, 0x000000, 0);
    hitArea.setInteractive();
    hitArea.setDepth(6);

    // Left-click handler - start fishing
    hitArea.on('pointerdown', (pointer: Phaser.Input.Pointer, _localX: number, _localY: number, event: Phaser.Types.Input.EventData) => {
      event.stopPropagation();
      
      if (pointer.leftButtonDown()) {
        // Left-click: start fishing
        this.callbacks.onFishingSpotClick(spotInfo);
      } else if (pointer.rightButtonDown() && this.callbacks.onFishingSpotRightClick) {
        // Right-click: research fishing spot
        const camera = this.scene.cameras.main;
        const screenX = worldX - camera.scrollX;
        const screenY = worldY - camera.scrollY;
        this.callbacks.onFishingSpotRightClick(spotInfo, screenX, screenY);
      }
    });

    // Hover effects
    hitArea.on('pointerover', () => {
      this.showSpotTooltip(spotInfo);
    });

    hitArea.on('pointerout', () => {
      this.hideSpotTooltip();
    });

    // Store the sprite
    this.spots.set(spot.id, {
      graphics,
      hitArea,
      chunkId,
      spotData: spotInfo,
    });
  }

  /**
   * Draw a fishing spot with animation
   */
  private drawFishingSpot(graphics: Phaser.GameObjects.Graphics, spot: FishingSpotInfo, phase: number): void {
    graphics.clear();

    const x = spot.tileX * TILE_SIZE;
    const y = spot.tileY * TILE_SIZE;
    const color = FISHING_SPOT_COLORS[spot.type] ?? 0x58a6ff; // Fallback to visible blue

    // Base tile with water effect
    graphics.fillStyle(color, 0.6);
    graphics.fillRect(x, y, TILE_SIZE, TILE_SIZE);

    // Animated ripple effect (concentric squares)
    const rippleSize = 2 + phase * 2;
    const rippleAlpha = 0.8 - phase * 0.15;
    
    graphics.lineStyle(1, color, rippleAlpha);
    graphics.strokeRect(
      x + TILE_SIZE / 2 - rippleSize,
      y + TILE_SIZE / 2 - rippleSize,
      rippleSize * 2,
      rippleSize * 2
    );

    // Center dot (fish indicator)
    const dotSize = 2 + (phase % 2);
    graphics.fillStyle(0xffffff, 0.9);
    graphics.fillRect(
      x + TILE_SIZE / 2 - dotSize / 2,
      y + TILE_SIZE / 2 - dotSize / 2,
      dotSize,
      dotSize
    );

    // Border
    graphics.lineStyle(1, color, 1);
    graphics.strokeRect(x, y, TILE_SIZE, TILE_SIZE);
  }

  private tooltipContainer: Phaser.GameObjects.Container | null = null;

  /**
   * Show tooltip for fishing spot
   */
  private showSpotTooltip(spot: FishingSpotInfo): void {
    this.hideSpotTooltip();

    const typeNames: Record<FishingSpotType, string> = {
      shallow_waters: 'Shallow Waters',
      deep_sea: 'Deep Sea',
      coral_reef: 'Coral Reef',
      fishing_hole: 'Fishing Hole',
      shipwreck_pool: 'Shipwreck Pool',
      volcanic_waters: 'Volcanic Waters',
      abyssal_trench: 'Abyssal Trench',
      whirlpool_edge: 'Whirlpool Edge',
      moonlit_shallows: 'Moonlit Shallows',
      ancient_grounds: 'Ancient Grounds',
    };

    const typeDescriptions: Record<FishingSpotType, string> = {
      shallow_waters: 'Common fish',
      deep_sea: 'Rare deep fish',
      coral_reef: 'Exotic fish',
      fishing_hole: 'Valuable catches',
      shipwreck_pool: 'Wreck-attracted fish',
      volcanic_waters: 'Geothermal waters',
      abyssal_trench: 'Legendary deep dwellers',
      whirlpool_edge: 'Violent currents, rare fish',
      moonlit_shallows: 'Celestial night fish',
      ancient_grounds: 'Mythic feeding ground',
    };

    const fallbackTitle = 'Fishing Spot';
    const fallbackDesc = 'Uncharted waters';
    const titleText = typeNames[spot.type] ?? fallbackTitle;
    const descText = typeDescriptions[spot.type] ?? fallbackDesc;
    const borderColor = FISHING_SPOT_COLORS[spot.type] ?? 0x58a6ff;

    this.tooltipContainer = this.scene.add.container(spot.worldX, spot.worldY - 25);
    this.tooltipContainer.setDepth(200);

    // Background
    const bg = this.scene.add.rectangle(0, 0, 110, 40, 0x000000, 0.85);
    bg.setStrokeStyle(1, borderColor);
    this.tooltipContainer.add(bg);

    // Title
    const title = this.scene.add.text(0, -5, titleText, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '10px',
      color: '#ffffff',
    });
    title.setOrigin(0.5);
    this.tooltipContainer.add(title);

    // Description
    const desc = this.scene.add.text(0, 6, descText, {
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '8px',
      color: '#aaaaaa',
    });
    desc.setOrigin(0.5);
    this.tooltipContainer.add(desc);
  }

  /**
   * Hide tooltip
   */
  private hideSpotTooltip(): void {
    if (this.tooltipContainer) {
      this.tooltipContainer.destroy();
      this.tooltipContainer = null;
    }
  }

  /**
   * Remove a specific fishing spot (e.g., when depleted)
   */
  removeSpot(spotId: string): void {
    const sprite = this.spots.get(spotId);
    if (sprite) {
      sprite.graphics.destroy();
      sprite.hitArea.destroy();
      sprite.label?.destroy();
      this.spots.delete(spotId);
      console.log(`🗑️ Removed depleted fishing spot: ${spotId}`);
    }
  }

  /**
   * Unload fishing spots for a chunk
   */
  unloadChunk(chunkId: string): void {
    const spotsToRemove: string[] = [];

    this.spots.forEach((sprite, spotId) => {
      if (sprite.chunkId === chunkId) {
        spotsToRemove.push(spotId);
      }
    });

    for (const spotId of spotsToRemove) {
      const sprite = this.spots.get(spotId);
      if (sprite) {
        sprite.graphics.destroy();
        sprite.hitArea.destroy();
        sprite.label?.destroy();
        this.spots.delete(spotId);
      }
    }
  }

  /**
   * Clean up all resources
   */
  destroy(): void {
    if (this.animationTimer) {
      this.animationTimer.destroy();
    }
    this.hideSpotTooltip();
    this.spots.forEach((sprite) => {
      sprite.graphics.destroy();
      sprite.hitArea.destroy();
      sprite.label?.destroy();
    });
    this.spots.clear();
  }
}
